import { useLayoutEffect, useRef, useState } from 'react';
import {
  BarChart3,
  CloudRain,
  Droplet,
  PieChart as PieChartIcon,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import useIsMobile from '../../../../hooks/use-is-mobile';
import { orDash } from '../../../../utils/schema-parse';
import type { RelatorioVisita } from '../../schemas/payload.schema';
import createBarAxisTick from './chuva-bar-axis-tick';
import createBarNameLabel from './chuva-bar-name-label';
import {
  BAR_AXIS_CHART_MIN_HEIGHT,
  BAR_AXIS_ROW_GAP,
  BAR_AXIS_WIDTH,
  BAR_CHART_EXTRA_HEIGHT,
  BAR_CHART_MIN_HEIGHT,
  BAR_NAME_CHAR_WIDTH,
  BAR_NAME_LINE_HEIGHT,
  BAR_NAME_OFFSET,
  BAR_ROW_GAP,
  BAR_SIZE,
  BAR_VALUE_SPACE,
  CHART_PALETTE,
  INK,
  MUTED,
} from './chuva-chart-constants';
import { formatMm, wrapLabel } from './chuva-chart-utils';
import renderPieLabel from './chuva-pie-label';
import ChuvaToggleButton from './chuva-toggle-button';
import ChuvaTooltip from './chuva-tooltip';

interface ChuvaSectionProps {
  chuva: RelatorioVisita['chuvaAcumulada'];
}

const ChuvaSection = ({ chuva }: ChuvaSectionProps) => {
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');
  const [chartWidth, setChartWidth] = useState(320);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const isMobile = useIsMobile();

  useLayoutEffect(() => {
    const node = chartRef.current;
    if (!node) return;

    setChartWidth(node.getBoundingClientRect().width);

    const supportsResizeObserver = typeof ResizeObserver !== 'undefined';
    if (!supportsResizeObserver) return;

    const observer = new ResizeObserver(([entry]) => {
      setChartWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [chartType]);

  if (!chuva) return null;

  const pluviometros = chuva.porPluviometro ?? [];
  const data = pluviometros.map((pluviometro) => ({
    name: orDash(pluviometro.descricao),
    value: pluviometro.totalMm ?? 0,
  }));

  const barNameSpace = isMobile
    ? chartWidth - BAR_VALUE_SPACE
    : BAR_AXIS_WIDTH - 12;
  const barNameMaxChars = Math.max(
    12,
    Math.floor(barNameSpace / BAR_NAME_CHAR_WIDTH),
  );
  const barNameLines = Math.max(
    1,
    ...data.map(
      (entry) => wrapLabel(entry.name.toUpperCase(), barNameMaxChars).length,
    ),
  );
  const barNameHeight = barNameLines * BAR_NAME_LINE_HEIGHT;
  const barRowHeight = isMobile
    ? BAR_SIZE + barNameHeight + BAR_ROW_GAP
    : Math.max(BAR_SIZE, barNameHeight) + BAR_AXIS_ROW_GAP;
  const barChartHeight = Math.max(
    isMobile ? BAR_CHART_MIN_HEIGHT : BAR_AXIS_CHART_MIN_HEIGHT,
    data.length * barRowHeight + BAR_CHART_EXTRA_HEIGHT,
  );

  return (
    <section className="rv-chuva">
      <div className="rv-chuva-header">
        <div className="rv-chuva-title-group">
          <CloudRain className="rv-chuva-title-icon" size={22} />
          <h2 className="rv-chuva-title">Acumulado de Chuva da Safra</h2>
        </div>

        <div className="rv-chuva-toggle-group">
          <ChuvaToggleButton
            active={chartType === 'pie'}
            icon={<PieChartIcon size={15} />}
            label="Pizza"
            onClick={() => setChartType('pie')}
          />
          <ChuvaToggleButton
            active={chartType === 'bar'}
            icon={<BarChart3 size={15} />}
            label="Barras"
            onClick={() => setChartType('bar')}
          />
        </div>
      </div>

      {data.length > 0 ? (
        <div className="rv-chuva-content">
          <div className="rv-chuva-chart">
            {chartType === 'pie' ? (
              <div className="rv-chuva-chart-canvas">
                <ResponsiveContainer height="100%" width="100%">
                  <PieChart>
                    <Tooltip content={<ChuvaTooltip />} />
                    <Pie
                      data={data}
                      dataKey="value"
                      innerRadius="55%"
                      isAnimationActive={!isMobile}
                      label={renderPieLabel}
                      labelLine={false}
                      nameKey="name"
                      outerRadius="80%"
                      paddingAngle={2}
                      stroke="rgb(var(--color-aqila-line))"
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_PALETTE[index % CHART_PALETTE.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div
                ref={chartRef}
                className="rv-chuva-chart-canvas"
                style={{ height: barChartHeight }}
              >
                <ResponsiveContainer height="100%" width="100%">
                  <BarChart
                    data={data}
                    layout="vertical"
                    margin={
                      isMobile
                        ? {
                            top: barNameHeight + BAR_NAME_OFFSET,
                            right: BAR_VALUE_SPACE,
                            bottom: 20,
                            left: 0,
                          }
                        : { top: 8, right: 16, bottom: 20, left: 8 }
                    }
                  >
                    <CartesianGrid
                      horizontal={false}
                      stroke="rgb(var(--color-aqila-line))"
                      strokeDasharray="4 4"
                    />
                    <XAxis
                      axisLine={false}
                      label={{
                        value: 'mm',
                        position: 'insideBottom',
                        offset: -8,
                        fill: MUTED,
                        fontSize: 12,
                      }}
                      tick={{ fill: MUTED, fontSize: 12 }}
                      tickLine={false}
                      type="number"
                    />
                    <YAxis
                      axisLine={false}
                      dataKey="name"
                      hide={isMobile}
                      interval={0}
                      tick={createBarAxisTick(barNameMaxChars)}
                      tickLine={false}
                      tickMargin={8}
                      type="category"
                      width={BAR_AXIS_WIDTH}
                    />
                    <Tooltip
                      content={<ChuvaTooltip />}
                      cursor={{ fill: 'rgb(var(--color-aqila-surface))' }}
                    />
                    <Bar
                      barSize={BAR_SIZE}
                      dataKey="value"
                      isAnimationActive={!isMobile}
                      radius={[0, 6, 6, 0]}
                    >
                      {isMobile ? (
                        <LabelList
                          content={createBarNameLabel(barNameMaxChars)}
                          dataKey="name"
                        />
                      ) : null}
                      {isMobile ? (
                        <LabelList
                          dataKey="value"
                          fill={INK}
                          fontSize={12}
                          fontWeight={700}
                          formatter={formatMm}
                          offset={8}
                          position="right"
                        />
                      ) : null}
                      {data.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_PALETTE[index % CHART_PALETTE.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          <div className="rv-chuva-side">
            <div>
              <span className="rv-chuva-label">Pluviômetros</span>
              <div className="rv-chuva-list">
                {pluviometros.map((pluviometro, index) => (
                  <div
                    key={pluviometro.id ?? pluviometro.descricao}
                    className="rv-chuva-row"
                  >
                    <span className="rv-chuva-row-name">
                      <span
                        className="rv-chuva-dot"
                        style={{
                          background:
                            CHART_PALETTE[index % CHART_PALETTE.length],
                        }}
                      />
                      {orDash(pluviometro.descricao)}
                    </span>
                    <span className="rv-chuva-row-value">
                      {formatMm(pluviometro.totalMm)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <span className="rv-chuva-label">Média Geral</span>
              <div className="rv-chuva-media">
                <Droplet className="rv-chuva-media-icon" size={18} />
                {formatMm(chuva.mediaGeralMm)}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="rv-chuva-empty">
          Sem dados de pluviômetros para o período.
        </p>
      )}
    </section>
  );
};

export default ChuvaSection;
